import type { IconComponent } from '@/components/icons'
import {
  ActivityIcon,
  ClipboardListIcon,
  FileMusicIcon,
  FlagIcon,
  HelpCircleIcon,
  HistoryIcon,
  LayoutDashboardIcon,
  MegaphoneIcon,
  MessageSquareIcon,
  SparklesIcon,
  ToggleLeftIcon,
  Settings2Icon,
  TagIcon,
  BadgeCopyIcon,
  UserSearchIcon,
  UsersIcon,
  WrenchIcon,
} from '@/components/icons'

export type AdminNavTo =
  | '/'
  | '/analytics'
  | '/users'
  | '/entitlements'
  | '/announcements'
  | '/songs'
  | '/reports'
  | '/feedback'
  | '/attribution-disputes'
  | '/reviews'
  | '/faq'
  | '/ops'
  | '/flags'
  | '/policy'
  | '/badge-copy'
  | '/tags'
  | '/audit'

export type AdminNavGroupId =
  | 'console'
  | 'triage'
  | 'people'
  | 'content'
  | 'system'

export type AdminNavGroup = {
  id: AdminNavGroupId
  label: string
}

/** Sidebar + overview section order. Operator jobs, not file paths. */
export const ADMIN_NAV_GROUPS: readonly AdminNavGroup[] = [
  { id: 'console', label: 'Console' },
  { id: 'triage', label: 'Triage' },
  { id: 'people', label: 'People' },
  { id: 'content', label: 'Content' },
  { id: 'system', label: 'System' },
]

/**
 * Layout a route first-paints as, so the auth gate can show a skeleton
 * shaped like the destination instead of one Overview card grid (#1054).
 */
export type AdminPendingShape = 'overview' | 'queue' | 'table' | 'form'

export type AdminNavItem = {
  to: AdminNavTo
  label: string
  /** Page header description */
  description: string
  icon: IconComponent
  exact: boolean
  group: AdminNavGroupId
  /** Skeleton shape while the staff session resolves. */
  pendingShape: AdminPendingShape
  adminOnly?: boolean
  /** Overview module card description */
  moduleDescription: string
  /** Show on overview module grid */
  showOnOverview?: boolean
}

/**
 * Single source of truth for ops navigation (#772).
 * Sidebar, shell descriptions, and overview modules derive from this list.
 */
export const ADMIN_NAV: readonly AdminNavItem[] = [
  {
    to: '/',
    pendingShape: 'overview',
    label: 'Overview',
    description: 'Quick look at health, queues, and module shortcuts',
    icon: LayoutDashboardIcon,
    exact: true,
    group: 'console',
    moduleDescription: 'Health metrics and module shortcuts.',
    showOnOverview: false,
  },
  {
    to: '/analytics',
    pendingShape: 'overview',
    label: 'Analytics',
    description: 'Operational trends, widgets, and filters',
    icon: ActivityIcon,
    exact: false,
    group: 'console',
    moduleDescription:
      '7–180 day charts with configurable widgets and dimension filters.',
    showOnOverview: true,
  },
  {
    to: '/users',
    pendingShape: 'table',
    label: 'Users',
    description: 'Search accounts, roles, bans, and sessions',
    icon: UsersIcon,
    exact: false,
    group: 'people',
    moduleDescription: 'Search accounts, set roles, ban, and revoke sessions.',
    showOnOverview: true,
  },
  {
    to: '/entitlements',
    pendingShape: 'table',
    label: 'Harmoni+',
    description: 'Comp and trial entitlements',
    icon: SparklesIcon,
    exact: false,
    adminOnly: true,
    group: 'people',
    moduleDescription: 'Comp and trial tooling access (library stays free).',
    showOnOverview: true,
  },
  {
    to: '/announcements',
    pendingShape: 'table',
    label: 'Banners',
    description: 'Schedule in-app announcements',
    icon: MegaphoneIcon,
    exact: false,
    group: 'content',
    moduleDescription: 'Schedule in-app announcements for product surfaces.',
    showOnOverview: true,
  },
  {
    to: '/songs',
    pendingShape: 'table',
    label: 'Songs',
    description: 'Find any chart and delete or restore it',
    icon: FileMusicIcon,
    exact: false,
    group: 'content',
    moduleDescription:
      'Search the catalog, including soft-deleted charts, and moderate without a report.',
    showOnOverview: true,
  },
  {
    to: '/reports',
    pendingShape: 'queue',
    label: 'Reports',
    description: 'Moderation queue for content reports',
    icon: FlagIcon,
    exact: false,
    group: 'triage',
    moduleDescription: 'Triage content reports from the community.',
    showOnOverview: true,
  },
  {
    to: '/feedback',
    pendingShape: 'queue',
    label: 'Feedback',
    description: 'In-app bug reports and suggestions from musicians',
    icon: MessageSquareIcon,
    exact: false,
    group: 'triage',
    moduleDescription: 'Triage in-app feedback: bugs, ideas, and notes.',
    showOnOverview: true,
  },
  {
    to: '/attribution-disputes',
    pendingShape: 'queue',
    label: 'Attribution',
    description: 'Review contributor credit disputes and roster changes',
    icon: UserSearchIcon,
    exact: false,
    group: 'triage',
    moduleDescription:
      'Resolve community attribution claims with an audit trail.',
    showOnOverview: true,
  },
  {
    to: '/reviews',
    pendingShape: 'queue',
    label: 'Reviews',
    description: 'Community contribution review queue',
    icon: ClipboardListIcon,
    exact: false,
    group: 'triage',
    moduleDescription: 'Approve or request changes on contributions.',
    showOnOverview: true,
  },
  {
    to: '/faq',
    pendingShape: 'table',
    label: 'Help',
    description: 'FAQ CMS for /help',
    icon: HelpCircleIcon,
    exact: false,
    group: 'content',
    moduleDescription: 'Edit public FAQ entries for the product help page.',
    showOnOverview: true,
  },
  {
    to: '/ops',
    pendingShape: 'form',
    label: 'Break-glass',
    description: 'Force-end jams and band ownership fixes',
    icon: WrenchIcon,
    exact: false,
    adminOnly: true,
    group: 'system',
    moduleDescription: 'Force-end live jams and transfer band ownership.',
    showOnOverview: true,
  },
  {
    to: '/flags',
    pendingShape: 'form',
    label: 'Flags',
    description: 'Maintenance, create gates, and product flags',
    icon: ToggleLeftIcon,
    exact: false,
    group: 'system',
    moduleDescription:
      'Operational feature control with impact preview and audit trail.',
    showOnOverview: true,
  },
  {
    to: '/policy',
    pendingShape: 'form',
    label: 'Policy',
    description: 'Reviewer bars, honorarium days, and arrangement caps',
    icon: Settings2Icon,
    exact: false,
    adminOnly: false,
    group: 'system',
    moduleDescription:
      'Tune numeric product policy without a redeploy. Audited writes.',
    showOnOverview: true,
  },
  {
    to: '/tags',
    pendingShape: 'form',
    label: 'Tags',
    description: 'Genres, moods, instruments, and themes',
    icon: TagIcon,
    exact: false,
    group: 'content',
    moduleDescription:
      'Curate the shared tag catalog used by Setup and song filters.',
    showOnOverview: true,
  },
  {
    to: '/badge-copy',
    pendingShape: 'form',
    label: 'Badge copy',
    description: 'Civic badge titles and descriptions only',
    icon: BadgeCopyIcon,
    exact: false,
    group: 'content',
    moduleDescription:
      'Overlay civic badge titles. Keys and unlock rules stay in code.',
    showOnOverview: true,
  },
  {
    to: '/audit',
    pendingShape: 'table',
    label: 'Audit Logs',
    description: 'Staff action history (read-only)',
    icon: HistoryIcon,
    exact: false,
    group: 'system',
    moduleDescription: 'Inspect audited staff actions (read surface).',
    showOnOverview: true,
  },
] as const

/** Nav entry owning a pathname, with the sidebar's exact/fuzzy semantics. */
export function navItemForPath(pathname: string): AdminNavItem | undefined {
  return ADMIN_NAV.find((nav) =>
    nav.exact
      ? pathname === nav.to
      : pathname === nav.to || pathname.startsWith(`${nav.to}/`),
  )
}

/**
 * Shell title + description for a route. Resolved by path, not by label —
 * matching on `staticData.title` let `/announcements` ("Announcements") miss
 * the nav entry ("Banners") and fall back to the generic description (#1055).
 * `fallbackTitle` only covers routes outside {@link ADMIN_NAV}.
 */
export function adminPageMeta(
  pathname: string,
  fallbackTitle?: string,
): {
  title: string
  description: string
} {
  const hit = navItemForPath(pathname)
  const normalized =
    fallbackTitle?.replace(/^Admin\s*·\s*/i, '').trim() || 'Overview'
  return {
    title: hit?.label ?? normalized,
    description: hit?.description ?? 'Staff tools for Harmoni production ops',
  }
}

export function overviewModules(isAdmin: boolean): AdminNavItem[] {
  return ADMIN_NAV.filter(
    (item) => item.showOnOverview && (!item.adminOnly || isAdmin),
  )
}

const GROUP_ITEM_ORDER: Record<AdminNavGroupId, readonly AdminNavTo[]> = {
  console: ['/', '/analytics'],
  triage: ['/reviews', '/reports', '/attribution-disputes', '/feedback'],
  people: ['/users', '/entitlements'],
  content: ['/songs', '/tags', '/badge-copy', '/announcements', '/faq'],
  system: ['/policy', '/flags', '/ops', '/audit'],
}

export type AdminNavGroupSection = {
  id: AdminNavGroupId
  label: string
  items: AdminNavItem[]
}

function itemsForGroup(
  groupId: AdminNavGroupId,
  items: readonly AdminNavItem[],
): AdminNavItem[] {
  const order = GROUP_ITEM_ORDER[groupId]
  return [...items]
    .filter((item) => item.group === groupId)
    .sort((a, b) => order.indexOf(a.to) - order.indexOf(b.to))
}

/** Sidebar sections: skip empty groups (e.g. System with no admin items). */
export function visibleNavGroups(isAdmin: boolean): AdminNavGroupSection[] {
  const visible = ADMIN_NAV.filter((item) => !item.adminOnly || isAdmin)
  return ADMIN_NAV_GROUPS.map((group) => ({
    ...group,
    items: itemsForGroup(group.id, visible),
  })).filter((section) => section.items.length > 0)
}

/** Overview jump grid, same grouping as the sidebar (no Overview tile). */
export function overviewModuleGroups(isAdmin: boolean): AdminNavGroupSection[] {
  const visible = overviewModules(isAdmin)
  return ADMIN_NAV_GROUPS.map((group) => ({
    ...group,
    items: itemsForGroup(group.id, visible),
  })).filter((section) => section.items.length > 0)
}

/**
 * Skeleton shape for a pathname. Unknown routes fall back to the console
 * overview, which is also what `/` renders.
 */
export function pendingShapeForPath(pathname: string): AdminPendingShape {
  return navItemForPath(pathname)?.pendingShape ?? 'overview'
}
